import type { Size } from '@/common/types'

export type BreakpointName = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl'

const BREAKPOINT_CSS_VARS: Record<BreakpointName, string> = {
  'xs': '--dds-breakpoints-xsmall',
  'sm': '--dds-breakpoints-small',
  'md': '--dds-breakpoints-medium',
  'lg': '--dds-breakpoints-large',
  'xl': '--dds-breakpoints-xlarge',
  '2xl': '--dds-breakpoints-2xlarge',
}

const BREAKPOINT_FALLBACK_PX: Record<BreakpointName, number> = {
  'xs': 320,
  'sm': 640,
  'md': 768,
  'lg': 1024,
  'xl': 1280,
  '2xl': 1536,
}

/** Narrowest to widest. */
const BREAKPOINT_ORDER: BreakpointName[] = ['xs', 'sm', 'md', 'lg', 'xl', '2xl']

function getBreakpointMediaQuery(breakpoint: BreakpointName): string | null {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return null
  }
  const cssVar = BREAKPOINT_CSS_VARS[breakpoint]
  const value = getComputedStyle(document.documentElement)
    .getPropertyValue(cssVar)
    .trim()
  const width = value || `${BREAKPOINT_FALLBACK_PX[breakpoint]}px`
  return `(max-width: ${width})`
}

/**
 * Map from breakpoint to component size. A key applies at that breakpoint and all narrower ones (e.g. md:lg → xs, sm, md use lg).
 */
export type BreakpointToSizeMap = Partial<Record<BreakpointName, Size>>

const VALID_SIZES: Size[] = ['xxs', 'xs', 'sm', 'md', 'lg']
const VALID_BREAKPOINTS: BreakpointName[] = [
  'xs',
  'sm',
  'md',
  'lg',
  'xl',
  '2xl',
]

/**
 * Parses a string like "sm:lg,md:md" into a BreakpointToSizeMap.
 * Key = breakpoint (xs, sm, md, lg, xl, 2xl). Value = component size (xxs, xs, sm, md, lg).
 * Invalid pairs are skipped. Returns undefined for empty/null/whitespace.
 */
export function parseResponsiveSizeMap(
  value: string | null | undefined,
): BreakpointToSizeMap | undefined {
  const s = value?.trim()
  if (!s) return undefined
  const map: BreakpointToSizeMap = {}
  const pairs = s.split(',').map(p => p.trim().split(':'))
  for (const [bpKey, sizeVal] of pairs) {
    if (!bpKey || !sizeVal) continue
    const breakpoint = VALID_BREAKPOINTS.includes(bpKey as BreakpointName)
      ? (bpKey as BreakpointName)
      : null
    if (breakpoint && VALID_SIZES.includes(sizeVal as Size)) {
      map[breakpoint] = sizeVal as Size
    }
  }
  return Object.keys(map).length > 0 ? map : undefined
}

/**
 * Returns the active breakpoint: the narrowest one that the viewport is currently below.
 * Returns null if viewport is above all breakpoints.
 */
function getActiveBreakpoint(): BreakpointName | null {
  if (typeof window === 'undefined') return null
  try {
    for (const bp of BREAKPOINT_ORDER) {
      const query = getBreakpointMediaQuery(bp)
      if (query && window.matchMedia(query).matches) return bp
    }
    return null
  } catch {
    return null
  }
}

/**
 * Returns the effective component size from the breakpoint→size map.
 * A map entry like "md:lg" applies at that breakpoint and all narrower breakpoints (xs, sm, md).
 * With multiple entries we use the narrowest matching key (most specific). E.g. "sm:lg,md:md" → xs/sm use lg, md uses md.
 */
export function getResponsiveSize(
  requestedSize: Size,
  breakpointToSizeMap: BreakpointToSizeMap | null | undefined,
): Size {
  if (!breakpointToSizeMap) return requestedSize
  const active = getActiveBreakpoint()
  if (active == null) return requestedSize
  const activeIndex = BREAKPOINT_ORDER.indexOf(active)
  for (let i = activeIndex; i < BREAKPOINT_ORDER.length; i++) {
    const bp = BREAKPOINT_ORDER[i]
    if (bp in breakpointToSizeMap) {
      return breakpointToSizeMap[bp] ?? requestedSize
    }
  }
  return requestedSize
}

/**
 * Subscribes to viewport changes at the given breakpoint. The callback is invoked
 * when the viewport crosses the breakpoint. Returns an unsubscribe function.
 */
export function subscribeToBreakpoint(
  breakpoint: BreakpointName,
  callback: () => void,
): () => void {
  const query = getBreakpointMediaQuery(breakpoint)
  if (!query) return () => {}

  try {
    const mql = window.matchMedia(query)
    const handler = () => callback()
    mql.addEventListener('change', handler)
    return () => mql.removeEventListener('change', handler)
  } catch {
    return () => {}
  }
}

/**
 * Subscribes to multiple breakpoints; callback runs when any of them is crossed.
 * Returns a single unsubscribe that removes all listeners.
 */
export function subscribeToBreakpoints(
  breakpoints: BreakpointName[],
  callback: () => void,
): () => void {
  if (breakpoints.length === 0) return () => {}
  const unsubs = breakpoints.map(bp => subscribeToBreakpoint(bp, callback))
  return () => unsubs.forEach(fn => fn())
}
